// src/pages/ResetPassword.jsx
import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';

import FormField from '../components/ui/FormField';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';
import { useUser } from '../contexts/UserContext';
import { EmailIcon, Lock, CheckIcon } from '../components/common/IconsComponent';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const ResetPassword = () => {
  const q = useQuery();
  const navigate = useNavigate();
  const { showAlert, isLoading, resetPassword } = useUser();

  const [done, setDone] = useState(false);

  const initialEmail = q.get('email') || '';
  const initialToken = q.get('token') || '';

  const schema = Yup.object({
    email: Yup.string().email('כתובת מייל לא תקינה').required('כתובת מייל נדרשת'),
    // token comes from URL (hidden), still validate it
    token: Yup.string().min(20, 'טוקן לא תקין').required('טוקן חסר'),
    newPassword: Yup.string()
      .min(8, 'סיסמה חייבת להכיל לפחות 8 תווים')
      .matches(/[A-Z]/, 'חייבת לכלול אות גדולה (A-Z)')
      .matches(/[a-z]/, 'חייבת לכלול אות קטנה (a-z)')
      .matches(/[0-9]/, 'חייבת לכלול ספרה (0-9)')
      .matches(/[!@#$%^&*(),.?":{}|<>]/, 'חייבת לכלול תו מיוחד')
      .required('סיסמה חדשה נדרשת'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'הסיסמאות אינן תואמות')
      .required('אימות סיסמה נדרש'),
  });

  const formik = useFormik({
    initialValues: {
      email: initialEmail,
      token: initialToken, // hidden
      newPassword: '',
      confirmPassword: '',
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        // if token missing from URL, show a friendly message
        if (!values.token) {
          setFieldError('email', 'קישור האיפוס לא תקין או חסר טוקן. בקש קישור חדש.');
          showAlert('קישור האיפוס לא תקין. בקש קישור חדש.', 'error');
          return;
        }

        await resetPassword({
          email: values.email,
          token: values.token,
          newPassword: values.newPassword,
        });

        setDone(true);
        showAlert('הסיסמה עודכנה בהצלחה. אפשר להתחבר כעת.', 'success');

        // ✅ navigate to AuthPage after success
        setTimeout(() => navigate('/AuthPage'), 700);
      } catch (err) {
        if (err?.message === 'VALIDATION_ERROR' && err?.validationErrors) {
          const ve = err.validationErrors;

          if (ve.email) setFieldError('email', ve.email);
          if (ve.token) {
            // token is hidden → show it as a general email error + alert
            setFieldError('email', 'קישור האיפוס לא תקין או פג תוקף. בקש קישור חדש.');
            showAlert('קישור האיפוס לא תקין או פג תוקף. בקש קישור חדש.', 'error');
          }
          if (ve.newPassword) setFieldError('newPassword', ve.newPassword);

          if (!ve.token) showAlert('יש שדות לא תקינים. בדוק את הטופס.', 'error');
        } else {
          showAlert(err?.message || 'שגיאה באיפוס הסיסמה', 'error');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="min-h-screen bg-gary-100 relative overflow-hidden" dir="rtl">
      {(isLoading || formik.isSubmitting) && (
        <SpinnerOverlay variant="glow" text="Please wait" dark={false} blur={false} />
      )}

      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-40 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute -bottom-32 left-40 w-96 h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>

            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">איפוס סיסמה</h2>
              <p className="text-sm text-gray-500">הזן סיסמה חדשה כדי לסיים את התהליך</p>
            </div>

            {done ? (
              <div className="text-center p-4 bg-green-50 rounded-xl border border-green-100">
                <p className="text-green-800 font-medium">הסיסמה עודכנה בהצלחה 🎉</p>
                <p className="text-sm text-green-700 mt-1">מעבירים אותך להתחברות…</p>
              </div>
            ) : (
              <form onSubmit={formik.handleSubmit} className="space-y-5">
                <FormField
                  label="כתובת מייל"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="הזן כתובת מייל"
                  icon={<EmailIcon />}
                  required
                  error={formik.touched.email && formik.errors.email}
                />

                {/* ✅ token is hidden */}
                <input type="hidden" name="token" value={formik.values.token} />

                <FormField
                  label="סיסמה חדשה"
                  name="newPassword"
                  type="password"
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="הזן סיסמה חדשה"
                  icon={<Lock />}
                  required
                  error={formik.touched.newPassword && formik.errors.newPassword}
                />

                <FormField
                  label="אימות סיסמה"
                  name="confirmPassword"
                  type="password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="הזן שוב"
                  icon={<CheckIcon />}
                  required
                  error={formik.touched.confirmPassword && formik.errors.confirmPassword}
                />

                <button
                  type="submit"
                  disabled={formik.isSubmitting || isLoading}
                  className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl disabled:opacity-60"
                >
                  {formik.isSubmitting || isLoading ? 'מעדכן...' : 'אפס סיסמה'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/AuthPage')}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    חזרה להתחברות
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm font-light">www.doroosonline.co.il</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
